import { GripVertical, PanelsTopLeft } from 'lucide-react';
import { useEffect, useRef } from 'react';
import Sortable from 'sortablejs';

import { t } from '../i18n';
import { $homeLayout, setHomeLayout, type HomeSection } from '../store/app.store';
import { useStore } from '../hooks/use-store';
import { SettingsCard } from './settings-card';

const sectionLabels: Record<HomeSection, Parameters<typeof t>[0]> = {
  popular: 'popular',
  today: 'todayUpdates',
  collectionRecords: 'collectionRecords',
  continue: 'watchHistory',
  watchLater: 'watchLater',
  collections: 'myCollections',
  random: 'randomAnime',
  recommendations: 'recommendations',
};

const LayoutItem = ({ section }: { section: HomeSection }) => (
  <div className="home-layout-item" data-home-layout-item={section}>
    <GripVertical className="home-layout-handle" />
    <span>{t(sectionLabels[section])}</span>
  </div>
);

export const HomeLayoutEditor = () => {
  const layout = useStore($homeLayout);
  const layoutRef = useRef(layout);
  const visibleRef = useRef<HTMLDivElement>(null);
  const hiddenRef = useRef<HTMLDivElement>(null);
  layoutRef.current = layout;

  useEffect(() => {
    const visible = visibleRef.current;
    const hidden = hiddenRef.current;
    if (!visible || !hidden) {
      return;
    }

    const readSections = (container: HTMLElement): HomeSection[] =>
      Array.from(container.querySelectorAll<HTMLElement>('[data-home-layout-item]'))
        .map((item) => item.dataset.homeLayoutItem)
        .filter((section): section is HomeSection => section !== undefined);
    const restoreReactOrder = (): void => {
      const items = new Map<HomeSection, HTMLElement>();
      for (const item of [...visible.children, ...hidden.children]) {
        if (!(item instanceof HTMLElement)) {
          continue;
        }
        const section = item.dataset.homeLayoutItem;
        if (section) {
          items.set(section as HomeSection, item);
        }
      }
      for (const section of layoutRef.current.visible) {
        const item = items.get(section);
        if (item) {
          visible.append(item);
        }
      }
      for (const section of layoutRef.current.hidden) {
        const item = items.get(section);
        if (item) {
          hidden.append(item);
        }
      }
    };
    const save = (): void => {
      const nextLayout = { visible: readSections(visible), hidden: readSections(hidden) };
      queueMicrotask(() => {
        restoreReactOrder();
        setHomeLayout(nextLayout);
      });
    };
    const options = {
      group: 'home-layout',
      animation: 180,
      handle: '.home-layout-handle',
      ghostClass: 'home-layout-ghost',
      chosenClass: 'home-layout-chosen',
      dragClass: 'home-layout-drag',
      onEnd: save,
    };
    const visibleSortable = Sortable.create(visible, options);
    const hiddenSortable = Sortable.create(hidden, options);
    return () => {
      visibleSortable.destroy();
      hiddenSortable.destroy();
    };
  }, []);

  return (
    <SettingsCard
      className="home-layout-card"
      icon={PanelsTopLeft}
      title={t('customizeHome')}
      description={t('customizeHomeDescription')}
    >
      <div className="home-layout-editor">
        <section>
          <h3>{t('visibleContent')}</h3>
          <div ref={visibleRef} className="home-layout-list">
            {layout.visible.map((section) => (
              <LayoutItem key={section} section={section} />
            ))}
          </div>
        </section>
        <section>
          <h3>{t('hiddenContent')}</h3>
          <div ref={hiddenRef} className="home-layout-list">
            {layout.hidden.map((section) => (
              <LayoutItem key={section} section={section} />
            ))}
          </div>
        </section>
      </div>
    </SettingsCard>
  );
};
